import { Button } from '@/components/Button';
import { Loading } from '@/components/Loading';
import { PhoneInput } from '@/components/PhoneInput';
import { Text } from '@/components/Text';
import { TextInput } from '@/components/TextInput';
import { useAuthStore } from '@/stores/useAuthStore';
import { User } from '@/types/user';
import { handleError } from '@/utils/handleError';
import { Form, Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

export const AuthUserProfileInformations = () => {
  const { user, updateAuthUser } = useAuthStore();
  const { t } = useTranslation();

  const validationSchema = yup.object({
    firstname: yup.string().required(t('common:validation.firstname.required')),
    lastname: yup.string().required(t('common:validation.lastname.required')),
    email: yup
      .string()
      .email(t('common:validation.email.format'))
      .required(t('common:validation.email.required')),
    phone: yup.string().required(t('common:validation.phone.required')),
  });

  const handleSubmit = async (
    values: User,
    { setErrors, setSubmitting }: FormikHelpers<User>,
  ) => {
    try {
      setSubmitting(true);
      await updateAuthUser(values);
    } catch (error) {
      handleError(error, setErrors);
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return <Loading />;

  return (
    <>
      <Text className="text-center font-medium text-sky-800 md:text-left">
        {t('userSettings:general.informations.title')}
      </Text>
      <Formik
        initialValues={user}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, errors, touched }) => (
          <Form className="mt-4 grid w-full grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
            <div className="space-y-1">
              <TextInput
                label={t('userSettings:general.informations.label.firstname')}
                name="firstname"
                type="text"
                placeholder={t(
                  'userSettings:general.informations.placeholder.firstname',
                )}
                error={!!touched.firstname && !!errors.firstname}
                isRequired
              />
            </div>
            <div className="space-y-1">
              <TextInput
                label={t('userSettings:general.informations.label.lastname')}
                name="lastname"
                type="text"
                placeholder={t(
                  'userSettings:general.informations.placeholder.lastname',
                )}
                error={!!touched.lastname && !!errors.lastname}
                isRequired
              />
              {touched.lastname && errors.lastname && (
                <div className="text-sm text-red-500">{errors.lastname}</div>
              )}
            </div>
            <div className="space-y-1">
              <PhoneInput
                isRequired
                label={t('userSettings:general.informations.label.phone')}
                placeholder={t(
                  'userSettings:general.informations.placeholder.phone',
                )}
                name="phone"
              />
            </div>
            <div className="space-y-1">
              <TextInput
                label={t('userSettings:general.informations.label.email')}
                name="email"
                type="email"
                placeholder={t(
                  'userSettings:general.informations.placeholder.email',
                )}
                error={!!touched.email && !!errors.email}
                isRequired
              />
              {touched.email && errors.email && (
                <div className="text-sm text-red-500">{errors.email}</div>
              )}
            </div>
            <div className="mt-4 sm:col-span-2">
              <Button
                disabled={isSubmitting}
                type="submit"
                className="w-full lg:w-auto"
              >
                {t('userSettings:general.informations.update')}
              </Button>
              <Text className="mt-2 text-sm font-light text-gray-light">
                {t('userSettings:general.informations.note')}
              </Text>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};
